import usePermissions from '@/hooks/usePermissions';
import { useGetOrganizationDetailsQuery, useGetOrganizationEmailSettingsQuery } from '@/services/organizations';
import { Alert, Box, Card, useOpenClose } from '@palmetto/palmetto-components';
import { memo, useCallback, useState } from 'react';

import { OrganizationEmailSettingCategories, OrganizationType, ProgramType, UserPermissions } from 'types';

import { ActiveDomesticContentTable } from './ActiveDomesticContentTable';
import { EditDomesticContentModal } from './EditDomesticContentModal';
import type { DomesticContentModifier } from './types';

interface ActiveDomesticContentProps {
  modifiers: DomesticContentModifier[];
  alias: string;
  programType: ProgramType;
  isLoading: boolean;
}

export const ActiveDomesticContent = memo(
  ({ modifiers, alias, programType, isLoading }: ActiveDomesticContentProps) => {
    const [selectedModifier, setSelectedModifier] = useState<DomesticContentModifier | null>(null);
    const {
      isOpen: isEditDomesticContentModal,
      handleClose: handleEditDomesticContentModalClose,
      handleOpen: handleEditDomesticContentModalOpen,
    } = useOpenClose();

    const { data: emailSettings } = useGetOrganizationEmailSettingsQuery({
      alias,
    });
    const { data: organization } = useGetOrganizationDetailsQuery({ alias });
    const hasSignatoryEmail =
      (emailSettings?.emailSettings?.find((s) => s.category === OrganizationEmailSettingCategories.signatory)?.emails
        ?.length ?? 0) > 0;
    const isMaterialsVendor = organization?.organizationTypes?.includes(OrganizationType.materials);
    const permissions = usePermissions();
    const isSignatory = permissions?.includes(UserPermissions.lightReachOrganizationSignatory) ?? false;
    const canEdit = isSignatory && hasSignatoryEmail;
    const handleEdit = useCallback(
      (modifier: DomesticContentModifier) => {
        setSelectedModifier(modifier);
        handleEditDomesticContentModalOpen();
      },
      [handleEditDomesticContentModalOpen],
    );

    return (
      <>
        {!hasSignatoryEmail && !isMaterialsVendor && (
          <Alert
            variant="warning"
            message="Domestic content settings cannot be edited without any signatory emails."
          />
        )}
        <Card padding="xs">
          <Box padding="lg">
            <Box fontWeight="medium" fontSize="lg">
              Active
            </Box>
          </Box>
          <Box margin="0 md">
            <ActiveDomesticContentTable
              modifiers={modifiers}
              isLoading={isLoading}
              onEdit={handleEdit}
              canEdit={canEdit}
            />
          </Box>
          <EditDomesticContentModal
            isOpen={isEditDomesticContentModal}
            handleClose={handleEditDomesticContentModalClose}
            modifier={selectedModifier}
            alias={alias}
            programType={programType}
          />
        </Card>
      </>
    );
  },
);

ActiveDomesticContent.displayName = 'ActiveDomesticContent';
